import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store/appStore'
import { useContentBundle } from '@/hooks/useContentBundle'
import { LevelType } from '@/types/api'
import { TreeChevronIcon } from '@/components/TreeChevronIcon'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { byCanonicalOrder } from '@/utils/contentOrder'

// ── Draft badge ───────────────────────────────────────────────────────────────
// A small amber pill badge that appears next to tree items (and PA rows that
// contain draft child practices). Horizontal space is tight, so the pill is
// kept as compact as possible while remaining readable.
// The native `title` provides a hover explanation without any extra dependency.
function DraftBadge({ tooltip, compact = false }: { tooltip: string; compact?: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={`tree-draft-badge${compact ? ' tree-draft-badge--compact' : ''}`}
      title={tooltip}
      aria-label={tooltip}
    >
      {compact ? '◈' : t('sidebar.draftBadgeLabel')}
    </span>
  )
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const idx = lowerText.indexOf(lowerQuery)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

export function ContentTree() {
  const { t } = useTranslation()
  const navigate = useGuardedNavigate()
  const location = useLocation()
  const {
    treeFilterText,
    selectedPracticeAreaId,
    setSelectedPracticeAreaId,
    setActiveSidebarNav,
    selectedModelViewId,
    selectedLevelId,
    levelFilterExact,
    expandedPracticeAreaIds,
    togglePracticeAreaExpanded,
    selectedCustomViewId,
    customViews,
    setMobileNavOpen,
  } = useAppStore()
  const { data: bundle, isLoading, isError } = useContentBundle()

  // Sync selected PA + expansion from URL on mount / route change.
  useEffect(() => {
    // Direct practice-area route
    const paMatch = location.pathname.match(/\/practice-area\/(\d+)/)
    if (paMatch) {
      const id = Number(paMatch[1])
      if (id !== selectedPracticeAreaId) {
        setSelectedPracticeAreaId(id)
      }
      if (!expandedPracticeAreaIds.has(id)) {
        togglePracticeAreaExpanded(id)
      }
      return
    }

    // Practice route — resolve the parent PA directly from practiceAreaId
    const practiceMatch = location.pathname.match(/\/practice\/(\d+)/)
    if (practiceMatch && bundle) {
      const practiceId = Number(practiceMatch[1])
      const practice = bundle.practices.find((p) => p.id === practiceId)
      if (practice) {
        const paId = practice.practiceAreaId
        if (paId !== selectedPracticeAreaId) {
          setSelectedPracticeAreaId(paId)
        }
        if (!expandedPracticeAreaIds.has(paId)) {
          togglePracticeAreaExpanded(paId)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, bundle])

  if (isLoading) {
    return (
      <div className="sidebar-tree sidebar-tree--loading" aria-busy="true">
        <div className="tree-loading-text">{t('sidebar.contentLoading')}</div>
      </div>
    )
  }

  if (isError || !bundle) {
    return (
      <div className="sidebar-tree sidebar-tree--error">
        <div className="tree-error-text">{t('sidebar.contentUnavailable')}</div>
      </div>
    )
  }

  // ─── Determine selected level type ────────────────────────────────────────
  const selectedLevel = selectedLevelId !== null
    ? bundle.levels.find((l) => l.id === selectedLevelId) ?? null
    : null
  const selectedLevelType: LevelType | null = selectedLevel?.category ?? null
  const isMddapLevel = selectedLevelType === LevelType.Mddap

  // ─── Step 1: Resolve the active custom view ───────────────────────────────
  const activeCustomView = selectedCustomViewId !== null
    ? customViews.find((cv) => cv.id === selectedCustomViewId) ?? null
    : null

  // ─── Step 2: Filter Practice Areas by view membership ────────────────────
  let practiceAreas = bundle.practiceAreas

  if (activeCustomView) {
    // Custom view mode: only show PAs that are part of this view
    const paIdsInView = new Set(activeCustomView.practiceAreas.map((cvpa) => cvpa.practiceAreaId))
    practiceAreas = practiceAreas.filter((pa) => paIdsInView.has(pa.id))
  } else if (selectedModelViewId !== null) {
    // System model view filter.
    // Net-new draft PAs that haven't been linked to any model view yet are passed
    // through regardless so they remain visible in draft preview mode.
    const paIdsInView = new Set(
      bundle.modelViewPracticeAreas
        .filter((mvpa) => mvpa.modelViewId === selectedModelViewId)
        .map((mvpa) => mvpa.practiceAreaId)
    )
    practiceAreas = practiceAreas.filter(
      (pa) => paIdsInView.has(pa.id) || pa.isDraft
    )
  }

  // ─── Step 2b: MDDAP level PA filter ──────────────────────────────────────
  // When an MDDAP level is selected, only show PAs that contain at least one
  // practice at *exactly* that level.
  if (isMddapLevel && selectedLevelId !== null) {
    const practiceIdsAtMddapLevel = new Set(
      bundle.practiceLevels
        .filter((pl) => pl.levelId === selectedLevelId)
        .map((pl) => pl.practiceId)
    )
    const paIdsWithMddapLevel = new Set(
      bundle.practices
        .filter((p) => practiceIdsAtMddapLevel.has(p.id))
        .map((p) => p.practiceAreaId)
    )
    practiceAreas = practiceAreas.filter((pa) => paIdsWithMddapLevel.has(pa.id))
  }

  // ─── Step 3: Compute highlighted PA IDs ──────────────────────────────────
  // Highlighting is only active for numeric levels when levelFilterExact is true.
  // For MDDAP levels, highlighting is not applicable (no highlight checkbox shown).
  let highlightedPaIds: Set<number> | null = null

  if (!isMddapLevel && levelFilterExact && selectedLevelId !== null) {
    const selectedSequence = selectedLevel?.sequence ?? 0

    // Collect PA IDs that have at least one practice at exactly the selected level
    const practiceIdsAtExactLevel = new Set(
      bundle.practiceLevels
        .filter((pl) => pl.levelId === selectedLevelId)
        .map((pl) => pl.practiceId)
    )
    const paIdsWithExactLevel = new Set(
      bundle.practices
        .filter((p) => practiceIdsAtExactLevel.has(p.id))
        .map((p) => p.practiceAreaId)
    )

    if (activeCustomView) {
      // In custom view mode, additionally require that the selected level is
      // within the CV-assigned cap for each PA (selected sequence ≤ cap sequence)
      highlightedPaIds = new Set(
        activeCustomView.practiceAreas
          .filter((cvpa) => {
            if (!paIdsWithExactLevel.has(cvpa.practiceAreaId)) return false
            const capLevel = bundle.levels.find((l) => l.id === cvpa.levelId)
            const capSequence = capLevel?.sequence ?? 0
            return selectedSequence <= capSequence
          })
          .map((cvpa) => cvpa.practiceAreaId)
      )
    } else {
      highlightedPaIds = paIdsWithExactLevel
    }
  }

  // ─── Step 4: Text filter ──────────────────────────────────────────────────
  const filterText = treeFilterText.trim().toLowerCase()

  const matchingPracticeIds: Set<number> = new Set()
  if (filterText) {
    bundle.practices.forEach((p) => {
      if (
        p.name.toLowerCase().includes(filterText) ||
        p.displayName.toLowerCase().includes(filterText) ||
        (p.statement ?? '').toLowerCase().includes(filterText)
      ) {
        matchingPracticeIds.add(p.id)
      }
    })
  }

  const filteredPracticeAreas = filterText
    ? practiceAreas.filter(
        (pa) =>
          pa.name.toLowerCase().includes(filterText) ||
          pa.acronym.toLowerCase().includes(filterText) ||
          pa.displayName.toLowerCase().includes(filterText) ||
          bundle.practices
            .filter((p) => p.practiceAreaId === pa.id)
            .some((p) => matchingPracticeIds.has(p.id))
      )
    : practiceAreas

  const sortedPAs = [...filteredPracticeAreas].sort(byCanonicalOrder)

  // Which practice / PA is currently active (from URL)
  const activePracticeMatch = location.pathname.match(/\/practice\/(\d+)/)
  const activePracticeId = activePracticeMatch ? Number(activePracticeMatch[1]) : null

  const activePaMatch = location.pathname.match(/\/practice-area\/(\d+)/)
  let activePracticeAreaId: number | null = activePaMatch ? Number(activePaMatch[1]) : null
  if (!activePracticeAreaId && activePracticeId) {
    const practice = bundle.practices.find((p) => p.id === activePracticeId)
    if (practice) activePracticeAreaId = practice.practiceAreaId
  }

  const handlePracticeAreaClick = (id: number) => {
    setSelectedPracticeAreaId(id)
    setActiveSidebarNav('tree')
    navigate(`/practice-area/${id}`)
    // Re-selecting the current item does not change the location, so the mobile
    // drawer's route effect would not fire. Dismiss it explicitly. No-op on desktop.
    setMobileNavOpen(false)
  }

  const handlePracticeClick = (id: number) => {
    setActiveSidebarNav('tree')
    navigate(`/practice/${id}`)
    setMobileNavOpen(false)
  }

  return (
    <div className="sidebar-tree" role="tree" aria-label={t('sidebar.practiceAreaNavTree')}>
      {sortedPAs.length === 0 ? (
        <div className="tree-empty-text">{t('sidebar.noMatches')}</div>
      ) : (
        sortedPAs.map((pa) => {
          const isExpanded = expandedPracticeAreaIds.has(pa.id)
          const isPaSelected = activePracticeAreaId === pa.id
          const isPaHighlighted = highlightedPaIds !== null && highlightedPaIds.has(pa.id)

          // ─── Step 5: Compute effective level cap for this PA's practice list ──
          // Custom view active → use the CV's assigned level for this PA as the cap.
          // Additionally, if the user has a global level selected, honour whichever
          // cap is lower (more restrictive).
          // No custom view → use the global selected level directly.
          // NOTE: For MDDAP levels, the cap logic below is bypassed in Step 6.
          const cvEntry = activeCustomView
            ? activeCustomView.practiceAreas.find((cvpa) => cvpa.practiceAreaId === pa.id) ?? null
            : null
          const cvLevelId = cvEntry?.levelId ?? null

          const effectiveLevelId = (() => {
            if (!activeCustomView) return selectedLevelId
            if (cvLevelId === null) return selectedLevelId
            if (selectedLevelId === null) return cvLevelId
            // Both set — pick the lower sequence (more restrictive)
            const cvSeq = bundle.levels.find((l) => l.id === cvLevelId)?.sequence ?? 0
            const selSeq = bundle.levels.find((l) => l.id === selectedLevelId)?.sequence ?? 0
            return cvSeq <= selSeq ? cvLevelId : selectedLevelId
          })()

          const paPractices = bundle.practices
            .filter((p) => p.practiceAreaId === pa.id)
            .sort(byCanonicalOrder)

          return (
            <div key={pa.id} className="tree-pa-node" role="treeitem" aria-expanded={isExpanded}>
              {/* Practice Area row */}
              <div
                className={`tree-item tree-item--pa${isPaSelected ? ' selected' : ''}${isPaHighlighted ? ' highlighted' : ''}`}
                tabIndex={0}
                onClick={() => {
                  if (!isExpanded) togglePracticeAreaExpanded(pa.id)
                  handlePracticeAreaClick(pa.id)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (!isExpanded) togglePracticeAreaExpanded(pa.id)
                    handlePracticeAreaClick(pa.id)
                  }
                }}
              >
                <span
                  className={`tree-chevron${isExpanded ? ' tree-chevron--open' : ''}`}
                  aria-hidden="true"
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePracticeAreaExpanded(pa.id)
                  }}
                >
                  <TreeChevronIcon />
                </span>
                <span className="tree-item-label">
                  {highlightMatch(`${pa.displayName || pa.name} (${pa.acronym})`, treeFilterText)}
                  {(() => {
                    // Show the DRAFT badge on the PA row if:
                    //  • the PA translation itself is a draft overlay, OR
                    //  • at least one child practice is a draft overlay
                    // This ensures draft content is visible even when the PA is collapsed.
                    const paHasDraft = pa.isDraft || paPractices.some((p) => p.isDraft)
                    if (!paHasDraft) return null
                    const allChildrenAreDraft = paPractices.length > 0 && paPractices.every((p) => p.isDraft)
                    const tooltip = pa.isDraft && allChildrenAreDraft
                      ? t('draftPreview.itemBadgePAAndPractices')
                      : pa.isDraft
                        ? t('draftPreview.itemBadgePA')
                        : t('draftPreview.itemBadgePracticesOnly', { count: paPractices.filter((p) => p.isDraft).length })
                    return <DraftBadge tooltip={tooltip} />
                  })()}
                </span>
              </div>

              {/* Nested practices grouped by level */}
              {isExpanded && (
                <div className="tree-children" role="group">
                  {(() => {
                    // ─── Step 6: Filter practices ─────────────────────────────────
                    let filteredPractices = paPractices

                    if (isMddapLevel && selectedLevelId !== null) {
                      // MDDAP: exact match — only show practices at exactly the selected level
                      filteredPractices = filteredPractices.filter((p) =>
                        bundle.practiceLevels.some(
                          (pl) => pl.practiceId === p.id && pl.levelId === selectedLevelId
                        )
                      )
                    } else if (effectiveLevelId !== null) {
                      // Numeric: cumulative (show all practices up to and including the
                      // effective level cap). levelFilterExact only controls highlighting.
                      const effectiveLevel = bundle.levels.find((l) => l.id === effectiveLevelId)
                      const effectiveSequence = effectiveLevel?.sequence ?? 0
                      const eligibleLevelIds = new Set(
                        bundle.levels
                          .filter((l) => l.sequence <= effectiveSequence)
                          .map((l) => l.id)
                      )
                      filteredPractices = filteredPractices.filter((p) => {
                        const levelIdsForPractice = bundle.practiceLevels
                          .filter((pl) => pl.practiceId === p.id)
                          .map((pl) => pl.levelId)
                        // Net-new draft practices have no level assignment yet — always show them
                        if (levelIdsForPractice.length === 0) return p.isDraft
                        return levelIdsForPractice.some((lid) => eligibleLevelIds.has(lid))
                      })
                    }

                    // Apply text filter
                    if (filterText) {
                      filteredPractices = filteredPractices.filter((p) => matchingPracticeIds.has(p.id))
                    }

                    if (filteredPractices.length === 0) return null

                    // Group by primary level for display.
                    // Practices with no level assignment (e.g. net-new draft practices)
                    // are collected under a sentinel key (-1) and rendered without a
                    // level header so they still appear in the tree.
                    const UNGROUPED_KEY = -1
                    const practicesByLevel = new Map<number, typeof filteredPractices>()
                    for (const p of filteredPractices) {
                      const levelEntries = bundle.practiceLevels
                        .filter((pl) => pl.practiceId === p.id)
                        .map((pl) => ({
                          levelId: pl.levelId,
                          sequence: bundle.levels.find((l) => l.id === pl.levelId)?.sequence ?? 0,
                        }))
                        .sort((a, b) => a.sequence - b.sequence)
                      const primaryLevelId = levelEntries[0]?.levelId ?? UNGROUPED_KEY
                      if (!practicesByLevel.has(primaryLevelId)) practicesByLevel.set(primaryLevelId, [])
                      practicesByLevel.get(primaryLevelId)!.push(p)
                    }

                    const sortedLevelIds = [...practicesByLevel.keys()].sort((a, b) => {
                      if (a === UNGROUPED_KEY) return 1   // ungrouped always last
                      if (b === UNGROUPED_KEY) return -1
                      const la = bundle.levels.find((l) => l.id === a)
                      const lb = bundle.levels.find((l) => l.id === b)
                      return (la?.sequence ?? 0) - (lb?.sequence ?? 0)
                    })

                    return sortedLevelIds.map((levelId) => {
                      const level = bundle.levels.find((l) => l.id === levelId)
                      const levelPractices = practicesByLevel.get(levelId) ?? []

                      return (
                        <div key={levelId} className="tree-group-node" role="treeitem">
                          {/* Render a level header only when there is a matching level;
                              ungrouped draft practices (levelId === -1) render without one */}
                          {level && (
                            <div className="tree-item tree-item--group">
                              <span className="tree-item-label tree-item-label--group">
                                {level.displayName}
                              </span>
                            </div>
                          )}

                          {levelPractices.map((p) => (
                            <div
                              key={p.id}
                              className={`tree-item tree-item--practice${activePracticeId === p.id ? ' selected' : ''}`}
                              tabIndex={0}
                              role="treeitem"
                              aria-selected={activePracticeId === p.id}
                              onClick={() => handlePracticeClick(p.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') handlePracticeClick(p.id)
                              }}
                            >
                              <span className="bullet" aria-hidden="true" />
                              <span className="tree-item-label tree-item-label--practice">
                                <span className="tree-practice-code">
                                  {highlightMatch(p.displayName || p.name, treeFilterText)}
                                </span>
                                {p.statement && (
                                  <>
                                    <span className="tree-practice-separator"> – </span>
                                    <span className="tree-practice-name">
                                      {highlightMatch(p.statement, treeFilterText)}
                                    </span>
                                  </>
                                )}
                                {p.isDraft && (
                                  <DraftBadge tooltip={t('draftPreview.itemBadgePractice')} />
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
